import axios from 'axios'
import { csrfToken } from '../utils/sessionCookie'

/**
 * Shared Axios instance for all API calls.
 *
 * No baseURL is set intentionally — every API call in api.js already builds
 * the full URL from constants.API_ROOT so the URLs are passed as-is.
 *
 * Auth: the session JWT travels in the HttpOnly `gemsby_session` cookie, which
 * this code cannot read — `withCredentials` is what makes the browser attach it,
 * and is required even for a same-site XHR to a different subdomain. There is no
 * Authorization header any more: the token used to be kept in localStorage,
 * where any script on the origin could lift it, and reading it back into a
 * header is exactly what the cookie exists to replace.
 *
 * CSRF: attaching credentials automatically means a page on another origin can
 * cause an authenticated request, so mutating calls echo the readable
 * `gemsby_csrf` cookie in X-XSRF-Token. See VerifyCsrfForCookieAuth server-side.
 *
 * Error normalisation: Axios wraps HTTP errors in a rejected promise with
 * `error.response.status`. The jsonapi.js wrapper re-exposes this as
 * `error.status` so legacy callers (auth.js handleTokenExpired) keep working.
 */
const httpClient = axios.create({
  headers: {
    'Content-Type': 'application/vnd.api+json',
    Accept: 'application/vnd.api+json',
  },
  withCredentials: true,
})

const SAFE_METHODS = ['get', 'head', 'options']

// Echo the CSRF cookie on anything that can mutate. Read per-request rather than
// once at module load: the cookie is reissued on every login and session
// refresh, and a stale value fails the server's comparison.
httpClient.interceptors.request.use(config => {
  const method = (config.method || 'get').toLowerCase()

  // Identifies this frontend so the API scopes its session cookies to it. All
  // three apps sit on one parent domain (AUTH_COOKIE_DOMAIN) and so receive each
  // other's cookies; without this the API reads one shared `gemsby_session` and
  // signing into the marketplace overwrote the admin session, which is why the
  // admin-only routes started answering 403. See App\Support\SessionScope.
  config.headers['X-Gemsby-App'] = 'admin'

  if (!SAFE_METHODS.includes(method)) {
    const token = csrfToken()
    if (token) {
      config.headers['X-XSRF-Token'] = token
    }
  }

  return config
})

export default httpClient
